import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { ArrowLeft, Users, Clock, Eye, Monitor, Smartphone, Tablet, TrendingUp, Calendar } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export const AdminAnalytics = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [recentVisits, setRecentVisits] = useState([]);

  useEffect(() => {
    if (!user) {
      navigate('/admin');
    } else {
      loadAnalytics();
    }
  }, [user, navigate]);

  const loadAnalytics = async () => {
    try {
      const [summaryRes, visitsRes] = await Promise.all([
        axios.get(`${API_BASE}/api/admin/analytics/summary`),
        axios.get(`${API_BASE}/api/admin/analytics/visits?limit=50`)
      ]);
      setAnalytics(summaryRes.data);
      setRecentVisits(visitsRes.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      toast.error('Error al cargar analytics');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds < 0) return '0s';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-AR', { 
      day: '2-digit', 
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPageName = (path) => {
    const names = {
      '/': 'Inicio',
      '/proyectos': 'Proyectos',
      '/contacto': 'Contacto',
      '/admin': 'Admin Login',
      '/admin/dashboard': 'Admin Dashboard',
      '/admin/settings': 'Admin Configuración',
      '/admin/projects': 'Admin Proyectos',
    };
    if (path.startsWith('/proyectos/')) {
      return `Proyecto: ${path.split('/')[2]}`;
    }
    return names[path] || path;
  };

  const getDeviceIcon = (type) => {
    switch (type) {
      case 'mobile': return <Smartphone size={16} />;
      case 'tablet': return <Tablet size={16} />;
      default: return <Monitor size={16} />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center">
        <div className="text-slate-600">Cargando analytics...</div>
      </div>
    );
  }

  const maxVisits = analytics?.visits_by_day?.length > 0 
    ? Math.max(...analytics.visits_by_day.map(d => d.visits)) 
    : 1;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      <header className="border-b border-slate-200 bg-white">
        <div className="container-safe flex h-16 items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin/dashboard" className="flex h-9 w-9 items-center justify-center rounded-lg hover:bg-slate-100">
              <ArrowLeft size={20} />
            </Link>
            <h1 className="text-lg font-semibold text-slate-900">Analytics</h1>
          </div>
          <button 
            onClick={loadAnalytics}
            className="text-sm text-slate-600 hover:text-slate-900"
          >
            Actualizar
          </button>
        </div>
      </header>

      <main className="container-safe py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Eye size={20} className="text-blue-600" />
              </div>
            </div>
            <div className="text-2xl font-bold text-slate-900">{analytics?.total_visits || 0}</div>
            <div className="text-sm text-slate-600">Visitas Totales</div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                <Users size={20} className="text-green-600" />
              </div>
            </div>
            <div className="text-2xl font-bold text-slate-900">{analytics?.unique_visitors || 0}</div>
            <div className="text-sm text-slate-600">Visitantes Únicos</div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                <Clock size={20} className="text-purple-600" />
              </div>
            </div>
            <div className="text-2xl font-bold text-slate-900">{formatDuration(analytics?.avg_duration || 0)}</div>
            <div className="text-sm text-slate-600">Tiempo Promedio</div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <TrendingUp size={20} className="text-amber-600" />
              </div>
            </div>
            <div className="text-2xl font-bold text-slate-900">{analytics?.visits_today || 0}</div>
            <div className="text-sm text-slate-600">Visitas Hoy</div>
          </div>
        </div>

        {/* Period Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
            <div className="text-xl font-bold text-slate-900">{analytics?.visits_today || 0}</div>
            <div className="text-xs text-slate-500">Hoy</div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
            <div className="text-xl font-bold text-slate-900">{analytics?.visits_this_week || 0}</div>
            <div className="text-xs text-slate-500">Esta Semana</div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
            <div className="text-xl font-bold text-slate-900">{analytics?.visits_this_month || 0}</div>
            <div className="text-xs text-slate-500">Este Mes</div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Chart - Visits by Day */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Calendar size={20} />
              Visitas Últimos 30 Días
            </h3>
            <div className="h-48 flex items-end gap-1">
              {analytics?.visits_by_day?.length > 0 ? (
                analytics.visits_by_day.map((day, idx) => (
                  <div 
                    key={idx} 
                    className="flex-1 group relative"
                  >
                    <div 
                      className="bg-blue-500 hover:bg-blue-600 transition-colors rounded-t"
                      style={{ 
                        height: `${Math.max((day.visits / maxVisits) * 100, 5)}%`,
                        minHeight: '4px'
                      }}
                    />
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block bg-slate-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                      {day.date}: {day.visits} visitas
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex-1 flex items-center justify-center text-slate-400">
                  Sin datos aún
                </div>
              )}
            </div>
            {analytics?.visits_by_day?.length > 0 && (
              <div className="flex justify-between text-xs text-slate-400 mt-2">
                <span>{analytics.visits_by_day[0]?.date}</span>
                <span>{analytics.visits_by_day[analytics.visits_by_day.length - 1]?.date}</span>
              </div>
            )}
          </div>

          {/* Devices */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Monitor size={20} />
              Dispositivos
            </h3>
            <div className="space-y-4">
              {Object.entries(analytics?.visits_by_device || {}).length > 0 ? (
                Object.entries(analytics.visits_by_device).map(([device, count]) => {
                  const total = Object.values(analytics.visits_by_device).reduce((a, b) => a + b, 0);
                  const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                  return (
                    <div key={device}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2 text-sm text-slate-700 capitalize">
                          {getDeviceIcon(device)}
                          {device === 'desktop' ? 'Escritorio' : device === 'mobile' ? 'Móvil' : 'Tablet'}
                        </div>
                        <span className="text-sm font-medium text-slate-900">{count} ({percentage}%)</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${
                            device === 'desktop' ? 'bg-blue-500' : 
                            device === 'mobile' ? 'bg-green-500' : 'bg-purple-500'
                          }`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center text-slate-400 py-8">Sin datos aún</div>
              )}
            </div>
          </div>
        </div>

        {/* Top Pages */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm mb-8">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Páginas Más Visitadas</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  <th className="pb-3">Página</th>
                  <th className="pb-3 text-right">Visitas</th>
                  <th className="pb-3 text-right">Tiempo Promedio</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {analytics?.top_pages?.length > 0 ? (
                  analytics.top_pages.map((page, idx) => (
                    <tr key={idx}>
                      <td className="py-3 text-sm text-slate-900">{getPageName(page.page)}</td>
                      <td className="py-3 text-sm text-slate-600 text-right">{page.visits}</td>
                      <td className="py-3 text-sm text-slate-600 text-right">{formatDuration(page.avg_duration)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3} className="py-8 text-center text-slate-400">Sin datos aún</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent Visits */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Visitas Recientes</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  <th className="pb-3">Fecha</th>
                  <th className="pb-3">Página</th>
                  <th className="pb-3">Dispositivo</th>
                  <th className="pb-3 text-right">Duración</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {recentVisits.length > 0 ? (
                  recentVisits.slice(0, 20).map((visit, idx) => (
                    <tr key={idx}>
                      <td className="py-3 text-sm text-slate-600">{formatDate(visit.timestamp)}</td>
                      <td className="py-3 text-sm text-slate-900">{getPageName(visit.page)}</td>
                      <td className="py-3 text-sm text-slate-600 capitalize flex items-center gap-1">
                        {getDeviceIcon(visit.device_type)}
                        {visit.device_type === 'desktop' ? 'Escritorio' : visit.device_type === 'mobile' ? 'Móvil' : 'Tablet'}
                      </td>
                      <td className="py-3 text-sm text-slate-600 text-right">{formatDuration(visit.duration)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="py-8 text-center text-slate-400">Sin visitas registradas aún</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};
